import { useContext } from 'react';
import { WebAPIContext } from '../../contexts/WebAPIContext';

/** 操作説明動画情報の型定義 */
export type HowToVideoType = {
  howToVideoId: string;
  howToVideoName: string;
  parentId: string | null;
  type: 'directory' | 'link';
  videoLink?: string;
  isAdminOnly?: boolean;
  sortOrder?: number;
  updateDate?: string;
};

/** API レスポンスの型定義 */
export type HowToVideoAPIResponse = {
  status: 'success' | 'failed' | 'expired';
  message: string;
};

/**
 * 操作説明動画API用カスタムフック
 */
export const useHowToVideoAPI = () => {
  const WebAPI = useContext(WebAPIContext);

  /**
   * レスポンス処理
   */
  const handleResponse = (result: any) => {
    if (result.errors?.length > 0) {
      throw new Error(result.errors[0].message || 'GraphQL error');
    }
    const data = Object.values(result.data || {})[0];
    if ((data as any)?.status === 'failed') {
      throw new Error((data as any).message || 'API returned failure');
    }
    return data;
  };

  /**
   * 操作説明動画一覧（画面名＋動画）を取得する
   */
  const getHowToVideo = async (): Promise<HowToVideoType[]> => {
    const result = await WebAPI.request({
      query: `
        query {
          getHowToVideo {
            howToVideoId
            howToVideoName
            parentId
            type
            videoLink
            isAdminOnly
            sortOrder
            updateDate
          }
        }`,
    });
    return handleResponse(result) as HowToVideoType[];
  };

  /**
   * 操作説明動画を新規登録する
   * @param input - 登録データ
   */
  const putHowToVideo = async (input: {
    howToVideoName: string;
    videoLink?: string;
    parentId: string | null;
    type: 'directory' | 'link';
    isAdminOnly?: boolean;
  }): Promise<HowToVideoAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation putHowToVideo($input: putHowToVideoInfo) {
          putHowToVideo(input: $input) {
            message
            status
          }
        }
      `,
      variables: {
        input,
      },
    });
    return handleResponse(result) as HowToVideoAPIResponse;
  };

  /**
   * 操作説明動画を更新する
   * @param input - 更新データ
   */
  const updateHowToVideo = async (input: {
    howToVideoId: string;
    howToVideoName: string;
    videoLink: string;
    isAdminOnly?: boolean;
    updateDate?: string;
  }): Promise<HowToVideoAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation updateHowToVideo($input: updateHowToVideoInfo!) {
          updateHowToVideo(input: $input) {
            message
            status
          }
        }
      `,
      variables: {
        input,
      },
    });
    return handleResponse(result) as HowToVideoAPIResponse;
  };

  /**
   * 操作説明動画を削除する
   * @param input - 削除データ
   */
  const deletedHowToVideo = async (input: { howToVideoId: string; updateDate?: string }): Promise<HowToVideoAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation deletedHowToVideo($input: deletedHowToVideoInfo!) {
          deletedHowToVideo(input: $input) {
            message
            status
          }
        }`,
      variables: {
        input,
      },
    });
    return handleResponse(result) as HowToVideoAPIResponse;
  };

  /**
   * 操作説明動画・ディレクトリの同一階層内の並び順を一括更新する
   * @param input - 並べ替えデータ（parentId: 対象階層、orderedIds: 並べ替え後の順序どおりのID配列）
   */
  const reorderHowToVideo = async (input: { parentId: string | null; orderedIds: string[] }): Promise<HowToVideoAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation reorderHowToVideo($input: reorderHowToVideoInfo!) {
          reorderHowToVideo(input: $input) {
            message
            status
          }
        }`,
      variables: {
        input,
      },
    });
    return handleResponse(result) as HowToVideoAPIResponse;
  };

  return {
    getHowToVideo,
    putHowToVideo,
    updateHowToVideo,
    deletedHowToVideo,
    reorderHowToVideo,
  };
};
